// src/utils/restDayUtils.js
// Rest day detection utilities for holiday pay eligibility

/**
 * Day name mapping for work schedule lookup
 */
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Check if a given date is a rest day for an employee based on their work schedule
 * @param {object} employee - Employee data with employment.workSchedule
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is a rest day (non-working day)
 */
export const isRestDayForEmployee = (employee, date) => {
  const dayOfWeek = date.getDay();
  const dayName = DAY_NAMES[dayOfWeek];

  const schedule = employee.employment?.workSchedule?.[dayName];

  // If no schedule defined, assume it's not a rest day
  if (!schedule) {
    return false;
  }

  // Rest day = NOT a working day
  return schedule.isWorkingDay === false;
};

/**
 * Get the day name for a given date
 * @param {Date} date - Date to get day name for
 * @returns {string} Day name (lowercase)
 */
export const getDayName = (date) => {
  return DAY_NAMES[date.getDay()];
};

/**
 * Check if employee has a valid work schedule configured
 * @param {object} employee - Employee data
 * @returns {boolean} True if work schedule exists and has at least one working day
 */
export const hasValidWorkSchedule = (employee) => {
  const schedule = employee.employment?.workSchedule;

  if (!schedule || typeof schedule !== 'object') {
    return false;
  }

  // Check if at least one day is configured as a working day
  return DAY_NAMES.some(day => schedule[day]?.isWorkingDay === true);
};

/**
 * Get all working days for an employee
 * @param {object} employee - Employee data
 * @returns {string[]} Array of working day names (e.g., ['monday', 'tuesday', ...])
 */
export const getWorkingDays = (employee) => {
  const schedule = employee.employment?.workSchedule;

  if (!schedule || typeof schedule !== 'object') {
    return [];
  }

  return DAY_NAMES.filter(day => schedule[day]?.isWorkingDay === true);
};

/**
 * Get all rest days for an employee
 * @param {object} employee - Employee data
 * @returns {string[]} Array of rest day names (e.g., ['saturday', 'sunday'])
 */
export const getRestDays = (employee) => {
  const schedule = employee.employment?.workSchedule;

  if (!schedule || typeof schedule !== 'object') {
    return [];
  }

  return DAY_NAMES.filter(day => schedule[day]?.isWorkingDay === false);
};

/**
 * Find the previous working day before a given date
 * @param {object} employee - Employee data
 * @param {Date} date - Reference date
 * @param {number} maxDaysBack - Maximum days to search back (default: 7)
 * @returns {Date|null} The previous working day date, or null if not found
 */
export const getPreviousWorkingDay = (employee, date, maxDaysBack = 7) => {
  const checkDate = new Date(date);

  for (let i = 1; i <= maxDaysBack; i++) {
    checkDate.setDate(checkDate.getDate() - 1);

    if (!isRestDayForEmployee(employee, checkDate)) {
      return new Date(checkDate);
    }
  }

  return null;
};

/**
 * Find the next working day after a given date
 * @param {object} employee - Employee data
 * @param {Date} date - Reference date
 * @param {number} maxDaysForward - Maximum days to search forward (default: 7)
 * @returns {Date|null} The next working day date, or null if not found
 */
export const getNextWorkingDay = (employee, date, maxDaysForward = 7) => {
  const checkDate = new Date(date);

  for (let i = 1; i <= maxDaysForward; i++) {
    checkDate.setDate(checkDate.getDate() + 1);

    if (!isRestDayForEmployee(employee, checkDate)) {
      return new Date(checkDate);
    }
  }

  return null;
};
